/* eslint-disable no-unused-vars */
import { Stack } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import { Box } from "@mui/system";
import _ from "lodash";
import { calcProPercentsBasedOnFilterAndDays, itemPercentage, percentageLabel } from "../../../../utilis/common";
import { calculateDiffBetweenPercentages, pickComparisonColor } from "../../../../utilis/percentage-comparison";
import CardItemTooltip from "../../../../components/shared/CardItemTooltip";
import ColorBox from "../../../../components/shared/dashboard/ColorBox";
import NoRecordFound from "../../../../components/shared/NoRecordFound";
import styles from "./DiagnosticsCardList.module.scss";
import DiagnosticsCardSkeleton from "./DiagnosticsCardSkeleton";
import { useSelector } from "react-redux";
import useLoadingToggle from "../../../../components/hooks/useLoadingToggle";
import CheckboxLoader from "../../../../components/shared/checkbox-loader/CheckboxLoader";
import useMergedData from "../../../../components/hooks/useMergedData";

const STYLES = {
	list: {
		width: "100%",
		bgcolor: "background.paper",
	},
	listItem: {
		padding: "5px"
	},
	listItemButton: {
		borderRadius: "8px",
		paddingLeft: 2,
		"@media screen and (max-width: 1424px)": {
			paddingLeft: 0.8,
		},
		paddingRight: { md: 1, lg: 2 },
	},
	titleContainer: {
		display: "flex",
		alignItems: "center"
	},
	responsiveBox: {
		display: "none",
		"@media screen and (max-width: 1600px)": {
			display: "inline",
		},
	},
	responsiveBoxLarge: {
		display: "inline",
		"@media screen and (max-width: 1601px)": {
			display: "none",
		},
	},
	checkboxContainer: {
		position: "relative",
		display: "inline-flex",
		alignItems: "center"
	},
	checkbox: {
		paddingRight: { md: 0.2, lg: 0.5 }
	},
	checkboxDisabled: {
		'&.MuiCheckbox-root.Mui-disabled': {
			cursor: 'not-allowed !important',
			pointerEvents: 'auto !important'
		},
		'&.MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root': {
			cursor: 'not-allowed !important'
		}
	}
};

// Helper functions to improve readability
const getItemComparison = (item, dataComparison, comparingAgainstDifferentFacility) => {
	if (comparingAgainstDifferentFacility) {
		return _.find(dataComparison, { label: item?.label });
	}
	return _.find(dataComparison, { _id: item._id });
};

const calculateItemPercentages = (item, itemComparison, averageCensus, averageCensusComparison) => {
	const percentageOfAverageCensus = itemPercentage(item.total, averageCensus, "number");
	const percentageOfAverageCensusComparison = itemPercentage(
		itemComparison?.total || 0,
		averageCensusComparison,
		"number"
	);
	
	return { percentageOfAverageCensus, percentageOfAverageCensusComparison };
};

const calculateComparisonData = (percentageOfAverageCensus, percentageOfAverageCensusComparison, filter, filterComparison, itemComparison, projectionDays) => {
	return calculateDiffBetweenPercentages(
		percentageOfAverageCensus,
		percentageOfAverageCensusComparison,
		false,
		filter,
		filterComparison,
		itemComparison?.total,
		projectionDays
	);
};

const shouldShowPercentageLabel = (isComparingAgainstAvgCensus, lockedTotalBy, isFirstItemInPriorityData, transferType) => {
	return isComparingAgainstAvgCensus ||
		lockedTotalBy === "census" ||
		(isFirstItemInPriorityData && (!transferType || transferType.length === 0));
};

const getPercentageDisplay = (item, filter, projectionDays, page, shouldShow) => {
	const calculatedPercentage = calcProPercentsBasedOnFilterAndDays(
		item.percentage ? item.percentage : itemPercentage(item.total, null, null, page),
		filter,
		projectionDays,
		true,
		shouldShow
	);
	
	return (
		<>
			<span className="ffmar-bold">
				{calcProPercentsBasedOnFilterAndDays(item.total, filter, projectionDays)}{" "}
			</span>
			<span className="ffmar-reg">
				{percentageLabel(calculatedPercentage)}
			</span>
		</>
	);
};

const DiagnosticsCardList = ({
	data,
	dataComparison,
	handleToggle,
	handleToggleAll,
	selectedItem = [],
	type,
	page,
	filter,
	filterComparison,
	averageCensusComparison,
	averageCensus,
	cardTitle,
	loading,
	projectionDays,
	priorityData,
	priorityNumber,
	transferType,
	isComparingAgainstAvgCensus,
	lockedTotalBy,
	searchValue,
	disableInteractions,
}) => {
	const isFirstItemInPriorityData = priorityNumber === 1;
	const { comparingAgainstDifferentFacility } = useSelector(state => state.comparison);
	const { loadingItems, handleToggleWithLoader } = useLoadingToggle();
	const mergedData = useMergedData(data, dataComparison);

	// Filter data once instead of inside map for better performance
	const filteredData = mergedData.filter((x) =>
		x.label.toLowerCase().includes((searchValue || "").toLowerCase())
	);
	
	if (loading) {
		return <DiagnosticsCardSkeleton />;
	}
	
	const handleItemClick = (item, selected) => {
		if (disableInteractions) return;
		handleToggleWithLoader(
			() => handleToggle({ item, type, isChecked: selected, cardTitle }), 
			item?._id
		);
	};
	
	const renderListItem = (item, index) => {
		const ID = item._id;
		const itemComparison = getItemComparison(item, dataComparison, comparingAgainstDifferentFacility);
		const labelId = `checkbox-list-label-${ID}`;
		const selected = selectedItem.indexOf(ID) !== -1;
		
		const { percentageOfAverageCensus, percentageOfAverageCensusComparison } = calculateItemPercentages(
			item, itemComparison, averageCensus, averageCensusComparison
		);
		
		const { percentageDiff, itemTotalComparisonScaled, numberOfDays } = calculateComparisonData(
			percentageOfAverageCensus, percentageOfAverageCensusComparison, filter, filterComparison, itemComparison, projectionDays
		);
		
		const comparisonColor = pickComparisonColor(percentageDiff);
		const shouldShow = shouldShowPercentageLabel(isComparingAgainstAvgCensus, lockedTotalBy, isFirstItemInPriorityData, transferType);
		
		return (
			<ListItem
				sx={STYLES.listItem}
				key={ID}
				dense={true}
				disablePadding={true}
				className={`${styles.listItem} ${selected ? styles.selected : ""}`}
			>
				<ListItemButton
					sx={STYLES.listItemButton}
					role={undefined}
					onClick={() => handleItemClick(item, selected)}
					dense
					className={styles.listItemButton}
				>
					<Stack direction="row" sx={{ width: "100%" }}>
						<Stack
							sx={{ width: "100%" }}
							direction="row"
							alignItems="center"
							justifyContent="space-between"
						>
							<div className={styles.title} style={STYLES.titleContainer}>
								{!loading && (
									<ColorBox
										sx={{ mr: "10px" }}
										color={comparisonColor}
										comparingAgainst={itemComparison?.total}
										comparingAgainstScaled={itemTotalComparisonScaled}
										numberOfDays={numberOfDays}
									/>
								)}
								<Box sx={STYLES.checkboxContainer}>
									<Checkbox
										edge="start"
										checked={selected}
										tabIndex={-1}
										disableRipple
										disabled={item?.isReadOnly || disableInteractions}
										inputProps={{ "aria-labelledby": labelId }}
										sx={{ 
											...STYLES.checkbox,
											...(loadingItems[item?._id] && { opacity: 0 }),
											...(item?.isReadOnly && STYLES.checkboxDisabled)
										}}
									/>
									<CheckboxLoader
										className="small-dots-left"
										isLoading={loadingItems[item?._id]}
									/>
								</Box>
								<CardItemTooltip item={item}>
									<span>{item.label} &nbsp;</span>
								</CardItemTooltip>
								<Box component="span" sx={STYLES.responsiveBox}>
									{getPercentageDisplay(item, filter, projectionDays, page, shouldShow)}
								</Box>
							</div>
							<Box component="span" sx={STYLES.responsiveBoxLarge}>
								<div className={styles.totalCount}>
									{getPercentageDisplay(item, filter, projectionDays, page, shouldShow)}
								</div>
							</Box>								
						</Stack>
					</Stack>
				</ListItemButton>
			</ListItem>
		);
	};
	
	return (
		<div className={styles.diagnosticsCardList}>
			<List
				sx={STYLES.list}
				dense={true}
				disablePadding={true}
			>
				{filteredData.length > 0 && filteredData.map(renderListItem)}
				{!loading && <NoRecordFound data={mergedData} />}
			</List>
		</div>
	);
};

export default DiagnosticsCardList;